import { Assert } from "../../Assert";
import { DataSource, IDataSource } from "../../Data/DataSource";
import { DataSourcePropertyValueChange } from "../../Data/DataSourcePropertyValueChange";
import { EntityCommandInformation } from "../../EntityCommandInformation";
import { ComparisonExpression } from "../../Expressions/ComparisonExpression";
import { PartialNullable, RadPromiseLike } from "../../Extensions";
import { ArrayHelper } from "../../Helpers/ArrayHelper";
import { ObjectHelper } from "../../Helpers/ObjectHelper";
import { PathHelper } from "../../Helpers/PathHelper";
import { IPromisePublic } from "../../Helpers/PromiseHelper";
import { StringHelper } from "../../Helpers/StringHelper";
import { $$, IJNativeEvent, JNative, JNativeStatic } from "../../JNative";
import { PermissionCacheService } from "../../PermissionCacheService";
import { PropertyChange } from "../../PropertyChange";
import { Property, PropertyBoolean, PropertyString } from "../../RadObject";
import { AjaxError } from "../../Server/AjaxError";
import { PermissionEvaluator } from "../../Server/DotNet/SeriouslyRAD.Framework.Data.Entities/Evaluation/Evaluators/PermissionEvaluator";
import { ClientAwareCategoryAttribute } from "../../Server/DotNet/SeriouslyRAD.Framework/ClientAwareCategoryAttribute";
import { ClientAwareDisplayNameAttribute } from "../../Server/DotNet/SeriouslyRAD.Framework/ClientAwareDisplayNameAttribute";
import { ITypeInfoMetadata } from "../../Server/DotNet/SeriouslyRAD.Framework/Reflection/ITypeInfoMetadata";
import { IAjaxRequest } from "../../Server/IAjaxRequest";
import { IODataBatchRequest } from "../../Server/IODataBatchRequest";
import { WebApiClientService } from "../../Server/WebApiClientService";
import { ServiceManager } from "../../ServiceManager";
import { Utility } from "../../Utility";
import { Accordion } from "../Controls/Accordion";
import { Button } from "../Controls/Button";
import { ConfirmSaveChangesDialog } from "../Controls/ConfirmSaveChangesDialog";
import { ControlRef } from "../Controls/ControlRef";
import { DetailsPageRelatedRecordsPanel } from "../Controls/DetailsPageRelatedRecordsPanel";
import { ErrorDialog } from "../Controls/ErrorDialog";
import { MessagePanel, MessagePanelTypeEnum } from "../Controls/MessagePanel";
import { PageFooter } from "../Controls/PageFooter";
import { PropertyEditor } from "../Controls/PropertyEditor";
import { PropertyEditorNew } from "../Controls/PropertyEditorNew";
import { TabPages } from "../Controls/TabPages";
import { ValidationPanel } from "../Controls/ValidationPanel";
import { DialogResultEnum } from "../Dialogs/DialogResultEnum";
import { ElementRef } from "../ElementRef";
import { JsxFragment } from "../JsxFragment";
import { PageManager } from "../PageManager";
import { IPage, Page, PagePropertyValues } from "./Page";
import { NAMESPACE } from "./_Namespace";

import "./DetailsPage.scss";

export interface ISaveBatchData
{
    afterSaveRecordRequests: IAjaxRequest[];
    beforeSaveRecordRequests: IAjaxRequest[];
    configureBatchRequest: (batchRequest: IODataBatchRequest) => void;
    promises: Promise<unknown>[];
}

export type DetailsPagePropertyValues =
    PagePropertyValues
    & {
        autoCloseOnCancelIfOpenedAsDialog: boolean;
        confirmCancelOfUnsavedChanges: boolean;
        defaultToEditMode: boolean;
        editInProgress: boolean;
        editMode: boolean;
        entityId: string;
        entityName: string;
        entityNameFriendly: string;
        entityNamePlural: string;
        entityNamePluralFriendly: string;
        entitySetName: string;
        entitySetNameFriendly: string;
        entitySetNamePlural: string;
        entitySetNamePluralFriendly: string;
        entitySupportsSoftDelete: boolean;
        isSaving: boolean;
        isSoftDeleted: boolean;
        loading: boolean;
        newRecord: boolean;
        recordId: string;
        showEntityIcon: boolean;
        usingPropertyEditorNew: boolean;
    };

export type DetailsPageProperties = {
    [name in keyof DetailsPagePropertyValues]: Property<DetailsPagePropertyValues[name]>
};

export interface IDetailsPage extends IPage, DetailsPageProperties
{
    dataSourcePropertyFromUrl?: string;

    _loadApplicationEntityPropertiesAsync(dataSource: IDataSource): Promise<unknown>;
}

const
    rules: { action?: (data: any, processRulesResult: { handled: boolean }) => void, entityName: string, event: string }[] = [];

export class DetailsPage<TTypeInfoMetadata extends ITypeInfoMetadata = ITypeInfoMetadata> extends Page implements IDetailsPage
{
    // #region Static

    public static override classNamespace = NAMESPACE + "DetailsPage";
    public static override className = "DetailsPage";

    // #region Constructor

    static {
        $$(() =>
        {
            $$(document).trigger("DetailsPage:registerRules", DetailsPage.registerRule);
        });
    }

    // #endregion

    // #region Public Methods

    public static registerRule(rule: { action?: (data: any, processRulesResult: { handled: boolean }) => void, entityName: string, event: string })
    {
        rules.push(rule);
    }

    // #endregion

    // #region Private Methods

    private static processRules(event: string, data: any)
    {
        const
            processRulesResult: { handled: boolean } = {
                handled: false
            };

        rules
            .filter(item =>
            {
                return (
                    item.entityName === data.page.entityNameGet()
                    && item.event === event
                );
            })
            .forEach(item =>
            {
                if (item.action)
                    item.action(data, processRulesResult);
            });

        return processRulesResult;
    }

    // #endregion

    // #region Overrides

    public static override createPropertyDefaults(): PartialNullable<DetailsPagePropertyValues>
    {
        return {
            ...super.createPropertyDefaults(),
            ...{
                autoCloseOnCancelIfOpenedAsDialog: false,
                confirmCancelOfUnsavedChanges: true,
                defaultToEditMode: false,
                editInProgress: false,
                editMode: false,
                isSaving: false,
                loading: false,
                showEntityIcon: false,
            }
        };
    }

    // #endregion

    // #endregion

    // #region Instance

    // #region Global Variables

    public _buttonCancelControlRef!: ControlRef<Button>;
    public _buttonCloseControlRef!: ControlRef<Button>;
    public _buttonEditControlRef!: ControlRef<Button>;
    public _buttonSaveControlRef!: ControlRef<Button>;
    public _buttonSaveAndCloseControlRef!: ControlRef<Button>;
    protected _iconElementRef!: ElementRef;
    protected _messagePanel!: MessagePanel;
    public _validationPanelControlRef!: ControlRef<ValidationPanel>;

    public dataSourcePropertyFromUrl?: string;
    protected $$separator?: JNative<HTMLElement>;
    protected accordion?: Accordion;
    public applicationEntityProperties?: any[];
    protected blockNavigation?: (promise: IPromisePublic) => boolean;
    protected queueCancelAsyncTimeoutId?: number;
    public tabPages?: TabPages;
    protected titlePaths: string[];

    // #endregion

    // #region Properties

    public autoCloseOnCancelIfOpenedAsDialog!: PropertyBoolean;
    public confirmCancelOfUnsavedChanges!: PropertyBoolean;
    public defaultToEditMode!: PropertyBoolean;
    public editInProgress!: PropertyBoolean;
    public editMode!: PropertyBoolean;
    public entityId!: PropertyString;
    public entityName!: PropertyString;
    public entityNameFriendly!: PropertyString;
    public entityNamePlural!: PropertyString;
    public entityNamePluralFriendly!: PropertyString;
    public entitySetName!: PropertyString;
    public entitySetNameFriendly!: PropertyString;
    public entitySetNamePlural!: PropertyString;
    public entitySetNamePluralFriendly!: PropertyString;
    public entitySupportsSoftDelete!: PropertyBoolean;
    public isSaving!: PropertyBoolean;
    public isSoftDeleted!: PropertyBoolean;
    public loading!: PropertyBoolean;
    public newRecord!: PropertyBoolean;
    public recordId!: PropertyString;
    public showEntityIcon!: PropertyBoolean;
    public usingPropertyEditorNew!: PropertyBoolean;

    // #endregion

    // #region Constructor

    constructor(propertyValues?: PartialNullable<DetailsPagePropertyValues>)
    {
        super(propertyValues);

        this.addCssClass("rad-control-details-page");
        this.titlePaths = [
            "title",
            "description",
            "name",
            "referenceNumber"
        ];
        this.queueCancelAsyncTimeoutId = 0;

        this.blockNavigation = blockedNavigationPromise =>
        {
            this._queueCancelAsync(blockedNavigationPromise);
            return true;
        };

        // Trigger Property Changes
        this.editMode.triggerChange();
        this.entityName.triggerChange();
        this.entitySetName.triggerChange();
        this.newRecord.triggerChange();
        this.showEntityIcon.triggerChange();
    }

    // #endregion

    // #region Public Methods

    public cancelAsync(blockedNavigationPromise?: IPromisePublic)
    {
        if (this.confirmCancelOfUnsavedChanges.get() && this.editInProgress.get())
        {
            return (
                ConfirmSaveChangesDialog.display()
                    .then(result =>
                    {
                        if (result.dialogResult === DialogResultEnum.yes)
                        {
                            return (
                                this.saveAsync(blockedNavigationPromise ? false : true)
                                    .then(() =>
                                    {
                                        if (blockedNavigationPromise)
                                            blockedNavigationPromise.resolve();
                                    })
                                    .catch(() => { /* Intentionally empty */ })
                            );
                        }
                        else if (result.dialogResult === DialogResultEnum.no)
                        {
                            this._unRegisterBlockNavigationFunction();

                            if (blockedNavigationPromise)
                            {
                                blockedNavigationPromise.resolve();
                                return;
                            }
                            else
                            {
                                if (this.openedAsDialog)
                                {
                                    if (this.newRecord.get() || this.autoCloseOnCancelIfOpenedAsDialog.get())
                                        return this.closeAsync();
                                }
                                else
                                {
                                    if (this.newRecord.get() || this.defaultToEditMode.get())
                                    {
                                        window.history.back();
                                        return Promise.resolve();
                                    }
                                }

                                return this._switchToModeAsync({ editMode: false, reload: true });
                            }
                        }
                    })
            );
        }

        if (this.openedAsDialog)
        {
            if (this.newRecord.get() || this.autoCloseOnCancelIfOpenedAsDialog.get())
                return this.closeAsync();
        }
        else
        {
            if (this.newRecord.get() || this.defaultToEditMode.get())
            {
                window.history.back();
                return Promise.resolve();
            }
        }

        return this._switchToModeAsync({ editMode: false, reload: true });
    }
    public editAsync()
    {
        return this._switchToModeAsync({ editMode: true, reload: true });
    }
    public getRecordIdFromUrl()
    {
        const
            url = this.url.get();

        if (!url)
            return null;

        return (
            ArrayHelper.last(
                StringHelper.trimEnd(
                    PathHelper
                        .getLocation(url)
                        .pathname,
                    "/"
                ).split("/")
            )
        );
    }
    public async loadRecordAsync()
    {
        const
            id = this.getRecordIdFromUrl(),
            navigationData = this.navigationData.get(),
            additionalHeaders: any = {};

        this.reset();

        if (id === "new")
        {
            this.newRecord.set(true);

            const
                guid = await Utility.instance.newGuidAsync();

            this.recordId.set(guid);

            const
                record = await this._createNewRecordAsync();

            record.id = guid;

            await this.setDataRecordAsync(record);
        }
        else if (StringHelper.isGuid(id))
        {
            this.newRecord.set(false);
            this.recordId.set(id);

            this.isSoftDeleted.set(!!(navigationData?.isSoftDeleted));

            if (this.isSoftDeleted.get())
                additionalHeaders["RAD-Option-Include-Soft-Deleted"] = "true";

            const
                entitySetName = this.entitySetName.get();

            Assert.isNotNullOrUndefined(entitySetName, "entitySetName");
            Assert.isNotNullOrUndefined(id, "id");

            const
                data =
                    await ServiceManager.getRequired(WebApiClientService)
                        .createEntityGetRequest(entitySetName, id, null, additionalHeaders)
                        .executeAsync();

            await this.setDataRecordAsync(data);
        }
    }
    public resetValidation()
    {
        this.controlsGet(true)
            .filter((item): item is PropertyEditor | PropertyEditorNew => item instanceof PropertyEditor || item instanceof PropertyEditorNew)
            .forEach(item =>
            {
                item.errorState.set(false);
                item.validationMessages.set(null);
            });

        this._validationPanelControlRef.current?.setErrorMessages(null);
    }
    public saveAndCloseAsync()
    {
        this.saveAsync(true).catch(() => { /* Intentionally empty */ });
    }
    public async saveAsync(close?: boolean, switchToViewMode: boolean = true): Promise<boolean>
    {
        const
            dataSource = this.dataSource.get(),
            url = this.url.get(),
            location = url ? PathHelper.getLocation(url) : null,
            pathnameParts = location?.pathname ? StringHelper.trimEnd(location?.pathname, "/").split("/") : [],
            batchData: ISaveBatchData = {
                afterSaveRecordRequests: [],
                beforeSaveRecordRequests: [],
                configureBatchRequest: function (batchRequest: IODataBatchRequest) { },
                promises: []
            },
            requests: IAjaxRequest[] = [];

        let
            record: any,
            reset = false;

        this.resetValidation();

        const
            validationData = {
                validationErrors: []
            };

        dataSource?.triggerHandler("validating", validationData);

        if (validationData.validationErrors.length)
        {
            this.setValidation(validationData.validationErrors);
            return false;
        }

        const
            beforeSaveChangesData = {
                promises: []
            };

        dataSource?.triggerHandler("beforeSaveChanges", beforeSaveChangesData);

        this.isSaving.set(true);

        try
        {
            await Promise.all(beforeSaveChangesData.promises);

            this._saveBatchCreating(batchData);
            this.trigger("creatingSaveBatch", batchData);
            await Promise.all(batchData.promises);

            const
                entitySetName = this.entitySetName.get(),
                data = dataSource?.data.get(),
                dataOriginal = dataSource?.dataOriginal.get();

            Assert.isNotNullOrUndefined(entitySetName, "entitySetName");
            Assert.isNotNullOrUndefined(data, "data");

            requests.push(...batchData.beforeSaveRecordRequests);
            requests.push(
                ServiceManager.getRequired(WebApiClientService)
                    .createEntitySaveRequest(
                        entitySetName,
                        data,
                        dataOriginal
                    )
            );
            requests.push(...batchData.afterSaveRecordRequests);

            const
                batchRequest =
                    ServiceManager.getRequired(WebApiClientService)
                        .createBatchRequest(requests);

            batchData.configureBatchRequest(batchRequest);

            const
                newData = await batchRequest.executeAsync();

            if (reset)
                this.reset();

            const
                afterSaveChangesData = {
                    promises: []
                };

            dataSource?.triggerHandler("afterSaveChanges", afterSaveChangesData);

            await Promise.all(afterSaveChangesData.promises);

            record = newData[batchData.beforeSaveRecordRequests.length].response.response;
            this.promiseData = record;
            await this.setDataRecordAsync(record);

            if (this.newRecord.get())
            {
                this.url.set(pathnameParts.map((item, index) => { return index < pathnameParts.length - 1 ? item : record.id; }).join("/") + "/" + location?.search);
                this.newRecord.set(false);
            }

            if (close)
            {
                this.dialogResult = DialogResultEnum.ok;
                reset = true;
                await this.closeAsync();
                return true;
            }

            const
                processRulesResult = DetailsPage.processRules("newRecordAdded", { page: this });

            if (processRulesResult.handled)
                return true;

            if (switchToViewMode)
            {
                await this._switchToModeAsync({ editMode: false, reload: false });

                if (reset)
                    this.reset();

                this._updateDetailsPageRelatedRecordsPanels();
            }
        }
        catch (error)
        {
            if (error instanceof AjaxError && error.type.get() === "ValidationApiException")
            {
                const
                    entityName = this.entityName.get(),
                    validationErrors = (error.validationErrors.get() ?? []),
                    validationErrorsForThisEntity =
                        validationErrors
                            .filter((item: any) => item.entityName === entityName);

                this.setValidation(validationErrorsForThisEntity.length ? validationErrorsForThisEntity : validationErrors);
            }

            return false;
        }
        finally
        {
            this.isSaving.set(false);
        }

        return true;
    }
    public async setDataRecordAsync(data: any)
    {
        const
            dataSource = new DataSource({ data: data ? data : {} });

        // TODO - Move to more specific class.
        await this._loadApplicationEntityPropertiesAsync(dataSource);
        this.dataSource.set(dataSource);
        this.editInProgress.set(false);
        this._updateDetailsPageRelatedRecordsPanels();

        await Promise.all(
            this.controlsGet(true)
                .filter((item): item is PropertyEditor | PropertyEditorNew => item instanceof PropertyEditor || item instanceof PropertyEditorNew)
                .map(item => item.loadDataAsync())
        );
    }
    public setValidation(validationErrors: { errorMessage: string, propertyName: string }[])
    {
        this.resetValidation();

        if (validationErrors.length === 0)
            return;

        const
            errorMessages: string[] = [],
            controls = this.controlsGet(true);

        let
            shouldPushErrorMessage = true;

        validationErrors.forEach(item =>
        {
            if (item.propertyName)
            {
                controls
                    .filter((control): control is PropertyEditor | PropertyEditorNew => control instanceof PropertyEditor || control instanceof PropertyEditorNew)
                    .filter(control => ArrayHelper.contains(item.propertyName.toLowerCase().split(","), control.dataSourceProperty.get()?.toLowerCase()))
                    .forEach(control =>
                    {
                        if (control.showInlineValidationMessages.get())
                        {
                            shouldPushErrorMessage = false;

                            const
                                controlValidationMessages = control.validationMessages.get(),
                                existingMessages = Array.isArray(controlValidationMessages) ? controlValidationMessages : [],
                                messages: string[] = [];

                            messages.push(...existingMessages);
                            messages.push(item.errorMessage);

                            control.validationMessages.set(messages);
                        }

                        control.errorState.set(true);
                    });
            }

            if (shouldPushErrorMessage)
                errorMessages.push(item.errorMessage);

            shouldPushErrorMessage = true;
        });

        this._validationPanelControlRef.current?.setErrorMessages(errorMessages);
    }

    // #endregion

    // #region Protected Methods

    protected _autoCloseOnCancelIfOpenedAsDialogChanged(change: PropertyChange<boolean>)
    {
        /* Intentionally empty */
    }
    protected _confirmCancelOfUnsavedChangesChanged(change: PropertyChange<boolean>)
    {
        /* Intentionally empty */
    }
    protected async _createNewRecordAsync(): Promise<any>
    {
        return {};
    }
    protected _defaultToEditModeChanged(change: PropertyChange<boolean>)
    {
        /* Intentionally empty */
    }
    protected _disableSaveButtons()
    {
        if (this._buttonSaveControlRef.current)
            this._buttonSaveControlRef.current.enabled.set(false);

        if (this._buttonSaveAndCloseControlRef.current)
            this._buttonSaveAndCloseControlRef.current.enabled.set(false);
    }
    protected _editInProgressChanged(change: PropertyChange<boolean>)
    {
        if (change.currentValue)
        {
            this._registerBlockNavigationFunction();
            this._enableSaveButtons();
        }
        else
        {
            this._unRegisterBlockNavigationFunction();
            this._disableSaveButtons();
        }
    }
    protected _editModeChanged(change: PropertyChange<boolean>)
    {
        if (change.currentValue)
        {
            this.addCssClass("rad-state-edit-mode");
            this.removeCssClass("rad-state-view-mode");
        }
        else
        {
            this.removeCssClass("rad-state-edit-mode");
            this.addCssClass("rad-state-view-mode");
        }

        this.trigger("editModeChanged", change);
    }
    protected _enableSaveButtons()
    {
        if (this._buttonSaveControlRef.current)
            this._buttonSaveControlRef.current.enabled.set(true);

        if (this._buttonSaveAndCloseControlRef.current)
            this._buttonSaveAndCloseControlRef.current.enabled.set(true);
    }
    protected _entityIdChanged(change: PropertyChange<string>)
    {
        /* Intentionally empty */
    }
    protected _entityNameChanged(change: PropertyChange<string>)
    {
        if (change.originalValue)
            this.removeCssClass(StringHelper.format("rad-control-details-page-{0}", StringHelper.toDashed(change.originalValue)));

        if (change.currentValue)
            this.addCssClass(StringHelper.format("rad-control-details-page-{0}", StringHelper.toDashed(change.currentValue)));
    }
    protected _entityNameFriendlyChanged(change: PropertyChange<string>)
    {
        /* Intentionally empty */
    }
    protected _entityNamePluralChanged(change: PropertyChange<string>)
    {
        /* Intentionally empty */
    }
    protected _entityNamePluralFriendlyChanged(change: PropertyChange<string>)
    {
        /* Intentionally empty */
    }
    protected _entitySetNameChanged(change: PropertyChange<string>)
    {
        // TODO: JS MERGE
        if (change.originalValue)
            this._iconElementRef.current.removeClass(StringHelper.format("rad-icon-{0}-32", change.originalValue.toLowerCase()));

        if (change.currentValue)
            this._iconElementRef.current.addClass(StringHelper.format("rad-icon-{0}-32", change.currentValue.toLowerCase()));
    }
    protected _entitySetNameFriendlyChanged(change: PropertyChange<string>)
    {
        /* Intentionally empty */
    }
    protected _entitySetNamePluralChanged(change: PropertyChange<string>)
    {
        /* Intentionally empty */
    }
    protected _entitySetNamePluralFriendlyChanged(change: PropertyChange<string>)
    {
        /* Intentionally empty */
    }
    protected _entitySupportsSoftDeleteChanged(change: PropertyChange<boolean>)
    {
        /* Intentionally empty */
    }
    protected _getDetailsPageRelatedRecordsPanels()
    {
        return (
            this.controlsGet(true)
                .filter((item): item is DetailsPageRelatedRecordsPanel => item instanceof DetailsPageRelatedRecordsPanel)
        );
    }
    protected _getEntityCommandInformation()
    {
        const
            pageDataSource = this.dataSource.get(),
            pageData = pageDataSource ? pageDataSource.data.get() : null;

        return new EntityCommandInformation({
            caller: this,
            entityName: this.entityName.get(),
            entityNameFriendly: this.entityNameFriendly.get(),
            entitySetName: this.entityNamePlural.get(),
            entitySetNameFriendly: this.entityNamePluralFriendly.get(),
            entitySupportsSoftDelete: this.entitySupportsSoftDelete.get(),
            record: pageData
        });
    }
    public _hasDetailsPageRelatedRecordsPanels() // TODO - should be protected
    {
        return (
            this._getDetailsPageRelatedRecordsPanels().length
        );
    }
    protected _hideLookupItemsByTypeIfChildLookupItem()
    {
        if (this.entityName.get() !== "LookupItem")
            return;

        const
            dataSource = this.dataSource.get();

        if (!dataSource || !dataSource.propertyValueByPathGet("typeLookupItemId"))
            return;

        if (this.accordion)
        {
            this.accordion
                .controlsGet()
                .filter(item => item.text.get() === "Lookup Items by Type")
                .forEach(item => { item.visible.set(false); });
        }
    }
    protected _isSavingChanged(change: PropertyChange<boolean>)
    {
        /* Intentionally empty */
    }
    protected _isSoftDeletedChanged(change: PropertyChange<boolean>)
    {
        if (change.currentValue)
            this._messagePanel.prependTo(this._containerElementRef.current);
        else
            this._messagePanel.detach();

        if (this._buttonEditControlRef.current)
            this._buttonEditControlRef.current.visible.set(!this.editMode.get() && !change.currentValue);
    }
    public _loadApplicationEntityPropertiesAsync(dataSource: IDataSource)
    {
        return new Promise((resolve, reject) =>
        {
            if (this.applicationEntityProperties)
                resolve(undefined);
            else
            {
                const
                    data = dataSource.data.get();

                let
                    applicationEntityId;

                switch (this.entitySetName.get())
                {
                    case "ApplicationEntities":
                        applicationEntityId = data.id;
                        break;
                    case "ApplicationEntityProperties":
                        applicationEntityId = data.applicationEntityId;
                        break;
                }

                if (applicationEntityId)
                {
                    ServiceManager.getRequired(WebApiClientService)
                        .createApiMethodRequest("DataService", "CallDataService", { id: "e616d5a7-cf0b-46de-8822-236a151b32e2", operation: "List", data: ObjectHelper.toJson({ applicationEntityId: applicationEntityId }) })
                        .executeAsync()
                        .then(results =>
                        {
                            this.applicationEntityProperties = results;
                            resolve(undefined);
                        })
                        .catch(error => { reject(error); });
                }
                else
                    resolve(undefined);
            }
        });
    }
    protected _loadingChanged(change: PropertyChange<boolean>)
    {
        /* Intentionally empty */
    }
    protected _newRecordChanged(change: PropertyChange<boolean>)
    {
        const
            detailsPageRecordPanel = this.$$element.get()?.find(".rad-control-details-page-record-panel").radControl();

        if (!detailsPageRecordPanel)
            return;

        detailsPageRecordPanel.newRecord.set(change.currentValue);
    }
    protected _queueCancelAsync(blockedNavigationPromise: IPromisePublic)
    {
        if (this.queueCancelAsyncTimeoutId)
            return;

        this.queueCancelAsyncTimeoutId = window.setTimeout(() =>
        {
            this.queueCancelAsyncTimeoutId = 0;
            this.cancelAsync(blockedNavigationPromise);
        }, 100);
    }
    protected _recordIdChanged(change: PropertyChange<string>)
    {
        /* Intentionally empty */
    }
    protected _registerBlockNavigationFunction()
    {
        if (this.blockNavigation)
            PageManager.instance?.registerBlockNavigationFunction(this.blockNavigation);
    }
    protected _saveBatchCreating(saveBatchData: ISaveBatchData)
    {
        /* Intentionally empty */
    }
    protected _setModeLayout(mode: boolean)
    {
        const
            detailsPageRecordPanel = this.$$element.get()?.find(".rad-control-details-page-record-panel").radControl();

        if (!detailsPageRecordPanel)
            return;

        if (this.editMode.get() === mode)
            return;

        this.editMode.set(mode);

        this._buttonSaveControlRef.current?.visible.set(mode && !this.isSoftDeleted.get());
        this._buttonSaveAndCloseControlRef.current?.visible.set(mode && !this.isSoftDeleted.get());
        this._buttonCancelControlRef.current?.visible.set(mode);
        this._buttonEditControlRef.current?.visible.set(!mode && !this.isSoftDeleted.get());
        this._buttonCloseControlRef.current?.visible.set(!mode);

        //if (this.editMode.get() === mode)
        //    return;

        //this.editMode.set(mode);
        //this.buttonSave.visible.set(mode && !this.isSoftDeleted.get());
        //this.buttonSaveAndClose.visible.set(mode && !this.isSoftDeleted.get());
        //this.buttonCancel.visible.set(mode);
        //this.buttonEdit.visible.set(!mode && !this.isSoftDeleted.get());
        //this.buttonClose.visible.set(!mode );

        //if (mode)
        //{
        //    if (this.accordion)
        //    {
        //        this.accordion
        //            .controlsGet()
        //            .where(item => !item.controlsGet(true).any(itemInner => itemInner instanceof PropertyEditor))
        //            .each(item => { item.hide(); });
        //    }
        //}
        //else
        //{
        //    if (this.accordion)
        //    {
        //        this.accordion
        //            .controlsGet()
        //            .each(item => { item.show(); });
        //    }
        //}

        //if (this.accordion)
        //{
        //    this.accordion
        //        .controlsGet()
        //        .where(item => item.visible.get())
        //        .each((item, index) => { if (index === 0) item.open(); else item.close(); });
        //}

        //this._hideLookupItemsByTypeIfChildLookupItem();
    }
    protected _showEntityIconChanged(change: PropertyChange<boolean>)
    {
        if (change.currentValue)
            this._iconElementRef.current.show();
        else
            this._iconElementRef.current.hide();
    }
    protected _switchToModeAsync(options: { editMode: boolean, reload: boolean })
    {
        return new Promise(resolve =>
        {
            function animateIn($$containerWrapper: JNative<HTMLElement>)
            {
                $$("body").scrollTop(0);

                $$containerWrapper
                    .animate({ opacity: 1 })
                    .promise()
                    .then(() => { resolve(undefined); })
            }

            Assert.isNotNullOrUndefined(this._containerWrapperElementRef, "this._containerWrapperElementRef");

            this._containerWrapperElementRef.current
                .animate({ opacity: 0 })
                .promise()
                .then(() =>
                {
                    this.resetValidation();

                    if (options.reload)
                    {
                        this.loadRecordAsync()
                            .then(() =>
                            {
                                this._setModeLayout(options.editMode);
                                Assert.isNotNullOrUndefined(this._containerWrapperElementRef, "this._containerWrapperElementRef");
                                animateIn(this._containerWrapperElementRef.current);
                            });
                    }
                    else
                    {
                        this._setModeLayout(options.editMode);
                        Assert.isNotNullOrUndefined(this._containerWrapperElementRef, "this._containerWrapperElementRef");
                        animateIn(this._containerWrapperElementRef.current);
                    }
                })
        });
    }
    protected _unRegisterBlockNavigationFunction()
    {
        if (this.blockNavigation)
            PageManager.instance?.unRegisterBlockNavigationFunction(this.blockNavigation);
    }
    protected _updateDetailsPageRelatedRecordsPanels()
    {
        const
            newRecord = this.newRecord.get();

        this._getDetailsPageRelatedRecordsPanels()
            .forEach(item =>
            {
                if (newRecord)
                    item.hide();
                else
                    item.show();
            });
    }
    protected _updateTitle()
    {
        const
            dataSource = this.dataSource.get(),
            data = dataSource ? dataSource.data.get() : null,
            entityNameFriendly = this.entityNameFriendly.get();

        if (!entityNameFriendly)
            return;

        const
            titlePath =
                data ? ArrayHelper.firstOrDefault(this.titlePaths, item => data[item]) : null,
            entityDescription = StringHelper.format(
                "{0}{1}",
                this.newRecord.get() ? "New " : StringHelper.empty,
                entityNameFriendly
            );

        this.title.set(
            titlePath
                ? StringHelper.format(
                    "{0}: {1}",
                    entityDescription,
                    data[titlePath]
                )
                : entityDescription
        );
    }

    // #endregion

    // #region Overrides

    protected override __initializeProperties(propertyValues: DetailsPagePropertyValues)
    {
        super.__initializeProperties(propertyValues);

        this._buttonCancelControlRef = new ControlRef();
        this._buttonCloseControlRef = new ControlRef();
        this._buttonEditControlRef = new ControlRef();
        this._buttonSaveControlRef = new ControlRef();
        this._buttonSaveAndCloseControlRef = new ControlRef();
        this._iconElementRef = new ElementRef();
        this._messagePanel = new MessagePanel({
            cssClass: "rad-message-panel-deleted-entity",
            icon: "trash",
            text: "This record has been deleted and is displayed for audit purposes only",
            title: "Deleted Record",
            type: MessagePanelTypeEnum.negative
        });
        this._validationPanelControlRef = new ControlRef();

        this.autoCloseOnCancelIfOpenedAsDialog = new PropertyBoolean(this, propertyValues.autoCloseOnCancelIfOpenedAsDialog, this._autoCloseOnCancelIfOpenedAsDialogChanged);
        this.confirmCancelOfUnsavedChanges = new PropertyBoolean(this, propertyValues.confirmCancelOfUnsavedChanges, this._confirmCancelOfUnsavedChangesChanged);
        this.defaultToEditMode = new PropertyBoolean(this, propertyValues.defaultToEditMode, this._defaultToEditModeChanged);
        this.editInProgress = new PropertyBoolean(this, propertyValues.editInProgress, this._editInProgressChanged);
        this.editMode = new PropertyBoolean(this, propertyValues.editMode, this._editModeChanged);
        this.entityId = new PropertyString(this, propertyValues.entityId, this._entityIdChanged);
        this.entityName = new PropertyString(this, propertyValues.entityName, this._entityNameChanged);
        this.entityNameFriendly = new PropertyString(this, propertyValues.entityNameFriendly, this._entityNameFriendlyChanged);
        this.entityNamePlural = new PropertyString(this, propertyValues.entityNamePlural, this._entityNamePluralChanged);
        this.entityNamePluralFriendly = new PropertyString(this, propertyValues.entityNamePluralFriendly, this._entityNamePluralFriendlyChanged);
        this.entitySetName = new PropertyString({
            context: this,
            initialValue: propertyValues.entitySetName,
            changeHandler: this._entitySetNameChanged,
            attributes: [
                new ClientAwareCategoryAttribute({ category: "Data" }),
                new ClientAwareDisplayNameAttribute({ displayName: "Entity Set Name" }),
            ],
        });
        this.entitySetNameFriendly = new PropertyString(this, propertyValues.entitySetNameFriendly, this._entitySetNameFriendlyChanged);
        this.entitySetNamePlural = new PropertyString(this, propertyValues.entitySetNamePlural, this._entitySetNamePluralChanged);
        this.entitySetNamePluralFriendly = new PropertyString(this, propertyValues.entitySetNamePluralFriendly, this._entitySetNamePluralFriendlyChanged);
        this.entitySupportsSoftDelete = new PropertyBoolean(this, propertyValues.entitySupportsSoftDelete, this._entitySupportsSoftDeleteChanged);
        this.isSaving = new PropertyBoolean(this, propertyValues.isSaving, this._isSavingChanged);
        this.isSoftDeleted = new PropertyBoolean(this, propertyValues.isSoftDeleted, this._isSoftDeletedChanged);
        this.loading = new PropertyBoolean(this, propertyValues.loading, this._loadingChanged);
        this.newRecord = new PropertyBoolean(this, propertyValues.newRecord, this._newRecordChanged);
        this.recordId = new PropertyString(this, propertyValues.recordId, this._recordIdChanged);
        this.showEntityIcon = new PropertyBoolean(this, propertyValues.showEntityIcon, this._showEntityIconChanged);
        this.usingPropertyEditorNew = new PropertyBoolean(this, propertyValues.usingPropertyEditorNew);

        this.editInProgress.overrideSet((value, next) =>
        {
            if (!this.editMode.get())
                value = false;

            next(value);
        });
        this.title.overrideSet((value, next) =>
        {
            this._titleTextElementRef.current.text(value);
            next(value);
        });
    }

    protected override async _activateAsync()
    {
        await this.loadRecordAsync();

        const
            entityName = this.entityName.get(),
            newRecord = this.newRecord.get() ?? false,
            defaultToEditMode = this.defaultToEditMode.get() ?? false;

        let
            hasUpdatePermission = false;

        if (entityName === "LookupItem" && this.dataSource.get()?.propertyValueByPathGet("isLinkedToEnum"))
            hasUpdatePermission = false;
        else
        {
            hasUpdatePermission =
                (!!entityName && ServiceManager.getOptional(PermissionCacheService)?.hasPermissionForEntity(entityName, PermissionEvaluator.permissionLevels.update)) ?? false;
        }

        this._setModeLayout(newRecord || (defaultToEditMode && hasUpdatePermission));

        this._buttonSaveControlRef.current?.visibleEvaluator
            .set(new PermissionEvaluator({
                entityName,
                permission: this.newRecord.get() ? PermissionEvaluator.permissionLevels.create : PermissionEvaluator.permissionLevels.update
            }));

        this._buttonSaveAndCloseControlRef.current?.visibleEvaluator
            .set(new PermissionEvaluator({
                entityName,
                permission: this.newRecord.get() ? PermissionEvaluator.permissionLevels.create : PermissionEvaluator.permissionLevels.update
            }));

        this._buttonEditControlRef.current?.visibleEvaluator.set(new PermissionEvaluator({ entityName: this.entityName.get(), permission: PermissionEvaluator.permissionLevels.update }));

        await super._activateAsync();
    }
    protected override _createElementComplete()
    {
        super._createElementComplete();

        this._buttonSaveControlRef.current?.hide();
        this._buttonSaveAndCloseControlRef.current?.hide();
        this._buttonCancelControlRef.current?.hide();

        this.tabPages = this._containerElementRef.current.find(">.rad-control-tab-pages").radControl();
        this.accordion = this._containerElementRef.current.find(".rad-control-accordion").radControl();

        $$(document).trigger("SeriouslyRAD.DetailsPage:childControlsCreated", this);
    }
    protected override _createElementHtml(children?: JsxFragment | string | (JsxFragment | string | undefined)[])
    {
        return (
            <div class="rad-control rad-control-container-control rad-control-page">
                <div ref={this._containerWrapperElementRef} class="rad-control-container-wrapper">
                    <div ref={this._containerInnerWrapperElementRef} class="rad-control-container-inner-wrapper">
                        {this._createHeaderHtml()}
                        <div class="rad-control-container ui container fluid">
                            <ValidationPanel ref={this._validationPanelControlRef} />
                            <div ref={this._containerElementRef} class="rad-control-container-inner">
                                {children ?? this._createChildrenHtml()}
                            </div>
                        </div>
                    </div>
                </div>
                {this._createFooterHtml()}
            </div>
        );
    }
    protected override _createFooterHtml()
    {
        return (
            <PageFooter ref={this._pageFooterControlRef}>
                <Button ref={this._buttonSaveControlRef} action="saveAsync" actionTargetSelector=".rad-control-page" cssClass="rad-action-save primary" inline={true} text="Save" />
                <Button ref={this._buttonSaveAndCloseControlRef} action="saveAndCloseAsync" actionTargetSelector=".rad-control-page" cssClass="rad-action-save-and-close primary" inline={true} text="Save & Close" />
                <Button ref={this._buttonCancelControlRef} action="cancelAsync" actionTargetSelector=".rad-control-page" cssClass="rad-action-cancel" inline={true} text="Cancel" />
                <Button ref={this._buttonEditControlRef} action="editAsync" actionTargetSelector=".rad-control-page" cssClass="rad-action-edit primary" inline={true} text="Edit" />
                <Button ref={this._buttonCloseControlRef} action="closeAsync" actionTargetSelector=".rad-control-page" cssClass="rad-action-close" inline={true} text="Close" />
            </PageFooter>
        );
    }
    protected override _createHeaderHtml()
    {
        return (
            <header ref={this._headerElementRef} class="rad-page-header ui container fluid">
                <div class="rad-page-header-inner-wrapper">
                    <h1 ref={this._titleElementRef} class="ui dividing header">
                        <span ref={this._titleTextElementRef} class="rad-title"></span>
                        <i ref={this._iconElementRef} class="icon">
                            <span></span>
                        </i>
                    </h1>
                </div>
            </header>
        );
    }
    protected override _dataSourceChanged(change: PropertyChange<DataSource>)
    {
        super._dataSourceChanged(change);
        this._hideLookupItemsByTypeIfChildLookupItem();
        this._updateTitle();
    }
    protected override _dialogViewChanged(change: PropertyChange<boolean>)
    {
        super._dialogViewChanged(change);

        if (change.currentValue)
        {
            const
                $$buttonCancel = this._pageFooterControlRef.current?.$$element.get()?.find(".rad-action-cancel");

            if (!this.$$separator)
                this.$$separator = JNativeStatic.create$$({ nodeName: "div", attributes: { "class": "separator" } });

            $$buttonCancel?.parent().prepend(this.$$separator);
            $$buttonCancel?.parent().prepend($$buttonCancel);
        }
    }
    protected override _dispose()
    {
        this._buttonSaveControlRef.current?.remove();
        this._buttonSaveAndCloseControlRef.current?.remove();
        this._buttonCancelControlRef.current?.remove();
        this._buttonEditControlRef.current?.remove();
        this._buttonCloseControlRef.current?.remove();
        this._messagePanel?.remove();

        super._dispose();
    }
    protected override async _loadAsync()
    {
        this.reset();
        this.loading.set(true);

        try
        {
            const
                data =
                    await (
                        ServiceManager.getRequired(WebApiClientService)
                            .createEntitySearchRequest(
                                "Entities",
                                {
                                    columns: ["id"],
                                    filter: new ComparisonExpression({
                                        path: "name",
                                        value: this.entityName.get()
                                    }),
                                }
                            )
                            .executeAsync()
                    );

            if (!data || !data.length)
            {
                const
                    errorMessage = StringHelper.format("Unable to load entity \"{0}\"", this.entityName.get());

                ErrorDialog.displayError(errorMessage);
                throw new Error(errorMessage);
            }

            this.entityId.set(data[0].id);

            return await super._loadAsync();
        }
        finally
        {
            this.loading.set(false);
        }
    }

    public override closeAsync(): RadPromiseLike<any>
    {
        return (
            super.closeAsync()
                .then(() =>
                {
                    this.editInProgress.set(false);
                })
        );
    }
    protected override onDataSourcePropertyValueChanged(evt: IJNativeEvent, change: DataSourcePropertyValueChange<any>)
    {
        super.onDataSourcePropertyValueChanged(evt, change);

        const
            self = evt.data.self as this;

        if (!self.loading.get())
            DetailsPage.processRules("dataSourcePropertyValueChanged", { page: self, change: change });

        if (ArrayHelper.contains(self.titlePaths, change.path))
            self._updateTitle();

        self.editInProgress.set(!self.dataSourcePropertyFromUrl || change.path !== self.dataSourcePropertyFromUrl);
    }
    public override reset()
    {
        super.reset();
        this._setModeLayout(false);
        this.resetValidation();
    }

    // #endregion

    // #endregion
}
